import { useGetDashboard } from "@workspace/api-client-react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffH = Math.floor(diffMs / 1000 / 60 / 60);
  if (diffH < 1) return "just now";
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function Dashboard() {
  const { data, isLoading } = useGetDashboard();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="text-destructive font-mono text-sm font-medium">Unable to load dashboard</div>
        <div className="text-muted-foreground text-xs text-center max-w-xs">
          There was a problem fetching your data. Check your connection or try refreshing the page.
        </div>
      </div>
    );
  }

  const statCards = [
    { label: "Active Squads", value: data.totalSquads },
    { label: "Runners", value: data.totalMembers },
    { label: "Total Check-ins", value: data.totalCheckins },
    { label: "Overall Adherence", value: `${data.overallAdherencePct}%` },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold mb-1">Dashboard</h1>
        <p className="text-muted-foreground text-sm">The collective pulse.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border mb-8">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="bg-card p-6"
          >
            <div className="font-display text-4xl font-bold text-foreground mb-1">{stat.value}</div>
            <div className="text-muted-foreground text-xs uppercase tracking-wider font-mono">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-border rounded">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                Squad Leaderboard
              </h2>
            </div>
            <div className="divide-y divide-border">
              {data.squadLeaderboard.length === 0 ? (
                <div className="px-5 py-8 text-center text-muted-foreground text-sm">
                  No squads yet. Create one to get started.
                </div>
              ) : (
                data.squadLeaderboard.map((squad, i) => (
                  <Link key={squad.squadId} href={`/squads/${squad.squadId}`}>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                      className="px-5 py-4 flex items-center gap-4 hover:bg-accent/30 transition-colors cursor-pointer"
                    >
                      <span className="font-mono text-xs text-muted-foreground w-5 text-right">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{squad.squadName}</div>
                        <div className="text-muted-foreground text-xs font-mono">
                          {squad.memberCount} runners · top streak {squad.topStreak}d
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div
                            className={cn(
                              "h-full rounded-full transition-all",
                              squad.adherencePct >= 80 ? "bg-primary" : squad.adherencePct >= 60 ? "bg-chart-2" : "bg-destructive/60"
                            )}
                            style={{ width: `${squad.adherencePct}%` }}
                          />
                        </div>
                        <span className="font-mono text-sm font-bold text-foreground w-12 text-right">
                          {squad.adherencePct}%
                        </span>
                      </div>
                    </motion.div>
                  </Link>
                ))
              )}
            </div>
          </div>

          <div className="border border-border rounded">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                Recent Activity
              </h2>
            </div>
            <div className="divide-y divide-border">
              {data.recentActivity.length === 0 ? (
                <div className="px-5 py-8 text-center text-muted-foreground text-sm">
                  No check-ins yet.
                </div>
              ) : (
                data.recentActivity.slice(0, 15).map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.15 + i * 0.03 }}
                    className="px-5 py-3 flex items-center gap-3"
                  >
                    <span
                      className={cn(
                        "w-2 h-2 rounded-full flex-shrink-0",
                        item.status === "crushed" ? "bg-primary" : "bg-border"
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium">{item.memberName}</span>
                      <span className="text-muted-foreground text-sm">
                        {" "}
                        {item.status === "crushed" ? "crushed it" : "skipped"}
                      </span>
                      {item.note && (
                        <span className="text-muted-foreground text-sm"> — {item.note}</span>
                      )}
                    </div>
                    <span className="text-muted-foreground text-xs font-mono flex-shrink-0">
                      {formatDate(item.createdAt)}
                    </span>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="border border-border rounded">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                Top Streaks
              </h2>
            </div>
            <div className="divide-y divide-border">
              {data.topStreaks.length === 0 ? (
                <div className="px-5 py-8 text-center text-muted-foreground text-sm">
                  No streaks yet.
                </div>
              ) : (
                data.topStreaks.map((entry, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + i * 0.07 }}
                    className="px-5 py-4 flex items-center gap-3"
                  >
                    <span className="font-mono text-xs text-muted-foreground w-5 text-right">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{entry.memberName}</div>
                      <div className="text-muted-foreground text-xs font-mono truncate">{entry.squadName}</div>
                    </div>
                    <span className="font-display text-2xl font-bold text-foreground">
                      {entry.currentStreak}
                      <span className="text-xs text-muted-foreground font-mono ml-0.5">d</span>
                    </span>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
